'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ApiError } from '@/lib/api';
import { getStoredUserRaw } from '@/lib/authStorage';
import MysteryRoomMapCard from './MysteryRoomMapCard';
import type {
  MrFriend,
  MrLobbyChatMessage,
  MrMapSummary,
  MrProfileResponse,
  MrLobbyResponse,
} from './runtime/net/mysteryRoomApi';
import {
  mrApiErrorMessage,
  mrApiLobbyChatList,
  mrApiLobbyChatSend,
  mrApiLobbyCreate,
  mrApiLobbyCurrent,
  mrApiLobbyDisband,
  mrApiLobbyLeave,
  mrApiLobbyPoll,
  mrApiLobbyReady,
  mrApiLobbySettings,
  mrApiLobbySlot,
} from './runtime/net/mysteryRoomApi';
import {
  formatMysteryRoomFriendPresence,
  isMysteryRoomFriendPlaying,
  sortMysteryRoomFriends,
} from './friendPresence';
import type { MysteryRoomMapDefinition } from './runtime/world/mapTypes';
import MysteryRoomJoinBrowser from './MysteryRoomJoinBrowser';
import { MYSTERY_ROOM_MAX_WANDER_BOTS, clampWanderBotCount } from './mysteryRoomWanderDefaults';
import { findMysteryRoomMapDefinition, getBuiltinMysteryRoomMapDefinitions } from './runtime/world/mapRegistry';
import { formatMysteryRoomPlayableModesShort, mysteryRoomMapSupportsGameMode } from './mysteryRoomMapModes';

const BUILTIN_MAP_ID = 'lisad_9_night_shift';
const SLOTS_PER_SIDE = 5;
const STORAGE_KEY = 'mysteryRoomLobbyConfig.v1';

/** Гость больше не в лобби (распуск/удаление) — уходим с экрана «лобби» на поиск. */
function mrIsForcedLobbyExitError(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 403 || error.status === 404);
}

type ActiveServerLobby = NonNullable<MrLobbyResponse['lobby']>;

type SlotKind = 'host' | 'human' | 'open' | 'bot' | 'empty';

type GameModeKey = 'coop' | 'survival' | 'stealth' | 'infection';
type MissionKey = 'evac' | 'last_voice' | 'cleanup';
type DifficultyKey = 'low' | 'mid' | 'high';
type LobbyTypeKey = 'public' | 'private';
type IslandJoinKey = 'standard' | 'fast' | 'lite';
type LevelKey = 'low' | 'mid' | 'high';

type MrLobbyP2pMenuOverrides = {
  mapId?: string;
  bots?: number;
  botsOnly?: boolean;
  gameMode?: GameModeKey;
  botDifficulty?: DifficultyKey;
  globalMission?: MissionKey;
  zombieWaveCount?: number;
  zombieWaveSize?: number;
};

type Props = {
  selectedMapTitle: string;
  selectedMapId: string;
  selectedMapKind: 'builtin' | 'custom';
  selectedMapDefinition: MysteryRoomMapDefinition;
  mapStatus: string;
  mapsAuthRequired: boolean;
  publishedMaps: MrMapSummary[];
  myMaps: MrMapSummary[];
  profile: MrProfileResponse['profile'] | null;
  friends: MrFriend[];
  friendsStatus: string;
  inviteStatusByFriendId: Record<number, string>;
  matchId?: string | null;
  onSelectBuiltin: () => void;
  onSelectCustomMap: (mapId: string) => void;
  onStartLobby: (options: {
    botCount?: number;
    botsOnly?: boolean;
    gameMode?: GameModeKey;
    botDifficulty?: DifficultyKey;
    globalMission?: MissionKey;
    zombieWaveCount?: number;
    zombieWaveSize?: number;
    mrLinkedLobbyCode?: string | null;
    syncedMrLobby?: ActiveServerLobby;
  }) => void;
  onJoinP2PLobby: (rawCodeOrUrl: string, menuOverrides?: MrLobbyP2pMenuOverrides | null) => void;
  onInviteFriend: (friend: MrFriend) => void;
  onBackHome: () => void;
  onOpenCatalog: () => void;
  onOpenEditor: () => void;
  onOpenAbout: () => void;
  onExitGame: () => void;
  /** Вкладка шапки лобби при открытии (из URL) */
  initialLobbyNavTab?: 'create' | 'find';
  /** Как восстанавливать активное серверное лобби при открытии экрана */
  currentLobbyMode?: 'any' | 'owned';
  /** Синхронизация вкладки с query-параметром lobbyTab */
  onLobbyNavTabChange?: (tab: 'create' | 'find') => void;
};

type LobbyConfig = {
  gameMode: GameModeKey;
  mission: MissionKey;
  botClass: number;
  difficulty: DifficultyKey;
  lobbyType: LobbyTypeKey;
  maxMatchTimeMin: number;
  waveCount: number;
  friendlyFire: boolean;
  islandJoin: IslandJoinKey;
  infectionPercent: number;
  zombieLevel: LevelKey;
  lootLevel: LevelKey;
  fogDistance: LevelKey;
  slotsA: SlotKind[];
  slotsB: SlotKind[];
};

type StoredMenuUser = {
  username?: string | null;
  display_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  avatar?: string | null;
  avatar_url?: string | null;
  avatar_display_url?: string | null;
};

type ChatMessage = {
  author: string;
  text: string;
  ts: number;
  system?: boolean;
  mine?: boolean;
  avatarUrl?: string | null;
};

type ServerLobbySlot = NonNullable<ActiveServerLobby['slots']>[number];
type DisplaySlotRow = {
  key: string;
  kind: SlotKind;
  index: number;
  player?: ServerLobbySlot['player'] | null;
  status?: string;
};

const DEFAULT_CONFIG: LobbyConfig = {
  gameMode: 'coop',
  mission: 'evac',
  botClass: 10,
  difficulty: 'high',
  lobbyType: 'public',
  maxMatchTimeMin: 30,
  waveCount: 10,
  friendlyFire: true,
  islandJoin: 'standard',
  infectionPercent: 75,
  zombieLevel: 'high',
  lootLevel: 'mid',
  fogDistance: 'low',
  slotsA: ['host', 'bot', 'bot', 'open', 'open'],
  slotsB: ['bot', 'bot', 'bot', 'open', 'open'],
};

function readStoredUserId(): number | null {
  if (typeof window === 'undefined') return null;
  const raw = getStoredUserRaw();
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { id?: unknown };
    const id = Number(parsed?.id);
    return Number.isFinite(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

function parseServerTs(value: string): number {
  const ts = Date.parse(value.includes('T') ? value : value.replace(' ', 'T'));
  return Number.isFinite(ts) ? ts : Date.now();
}

const GAME_MODE_OPTIONS: { key: GameModeKey; icon: string; title: string; desc: string }[] = [
  { key: 'coop', icon: '⛨', title: 'Кооператив', desc: 'Команда против ИИ' },
  { key: 'survival', icon: '⏳', title: 'Выживание', desc: 'Держитесь как можно дольше' },
  { key: 'stealth', icon: '◈', title: 'Скрытность', desc: 'Тени и тактика' },
  { key: 'infection', icon: '☣', title: 'Заражение', desc: 'Игра против игрока' },
];

const MISSION_OPTIONS: { key: MissionKey; icon: string; title: string; desc: string }[] = [
  { key: 'evac', icon: '☣', title: 'Эвакуация образца', desc: 'Доставьте образец и покиньте объект' },
  { key: 'last_voice', icon: '〰', title: 'Последний голос', desc: 'Найдите источник сигнала' },
  { key: 'cleanup', icon: '✛', title: 'Зачистка сектора', desc: 'Устраните все угрозы в зоне' },
];

const DIFFICULTY_LABEL: Record<DifficultyKey, string> = { low: 'НИЗКАЯ', mid: 'СРЕДНЯЯ', high: 'ВЫСОКАЯ' };
/** Подписи ИИ в боковой панели ботов */
const BOT_AI_SEG_LABEL: Record<DifficultyKey, string> = { low: 'ЛЕГКО', mid: 'НОРМАЛ', high: 'СЛОЖНО' };
const SLOT_ROLE_OPTIONS = ['Штурмовик', 'Поддержка', 'Медик', 'Разведчик'] as const;
const SLOT_LOADOUT_OPTIONS = ['Штурмовой набор', 'Универсальный', 'Тихий заход', 'Тяжёлый'] as const;
const ISLAND_LABEL: Record<IslandJoinKey, string> = { standard: 'СТАНДАРТНОЕ', fast: 'БЫСТРОЕ', lite: 'ОБЛЕГЧЁННОЕ' };
const LEVEL_LABEL: Record<LevelKey, string> = { low: 'НИЗКАЯ', mid: 'СРЕДНИЙ', high: 'ВЫСОКАЯ' };

function mapLobbyGameModeForApi(mode: GameModeKey): string {
  switch (mode) {
    case 'survival':
      return 'incident_drill';
    case 'stealth':
    case 'infection':
      return 'sabotage';
    default:
      return 'classic';
  }
}

function mapIncidentForApi(level: LevelKey): 'calm' | 'standard' | 'critical' {
  if (level === 'low') return 'calm';
  if (level === 'high') return 'critical';
  return 'standard';
}

function mapBotDifficultyForApi(d: DifficultyKey): 'easy' | 'normal' | 'hard' {
  if (d === 'low') return 'easy';
  if (d === 'high') return 'hard';
  return 'normal';
}

function mapLobbyGameModeFromApi(mode: unknown): GameModeKey {
  if (mode === 'incident_drill') return 'survival';
  if (mode === 'sabotage') return 'stealth';
  return 'coop';
}

function mapBotDifficultyFromApi(difficulty: unknown): DifficultyKey {
  if (difficulty === 'easy') return 'low';
  if (difficulty === 'hard') return 'high';
  return 'mid';
}

function mapIncidentFromApi(level: unknown): LevelKey {
  if (level === 'calm') return 'low';
  if (level === 'critical') return 'high';
  return 'mid';
}

function mergeLobbyConfigFromServer(lobby: ActiveServerLobby | null, fallback: LobbyConfig): LobbyConfig {
  if (!lobby) return fallback;
  return {
    ...fallback,
    gameMode: mapLobbyGameModeFromApi(lobby.settings?.gameMode),
    difficulty: mapBotDifficultyFromApi(lobby.settings?.botDifficulty),
    zombieLevel: mapIncidentFromApi(lobby.settings?.incidentLevel),
    lobbyType: lobby.isPrivate ? 'private' : 'public',
  };
}

/** Параметры URL для P2P после старта MR-матча: режим/карту берём из снимка лобби сервера. */
function buildMrLobbyP2pMenuOverrides(lobbySnapshot: ActiveServerLobby): MrLobbyP2pMenuOverrides {
  const merged = mergeLobbyConfigFromServer(lobbySnapshot, DEFAULT_CONFIG);
  const bots = lobbySnapshot.players.filter((p) => p.isBot).length;
  const openSlots = (lobbySnapshot.slots ?? []).filter((s) => s.status === 'open' && !s.player).length;
  const humans = lobbySnapshot.players.filter((p) => !p.isBot).length;
  return {
    mapId: lobbySnapshot.mapId ?? undefined,
    bots,
    botsOnly: openSlots === 0 && bots > 0 && humans <= 1,
    gameMode: merged.gameMode,
    botDifficulty: merged.difficulty,
    globalMission: DEFAULT_CONFIG.mission,
    zombieWaveCount: DEFAULT_CONFIG.waveCount,
    zombieWaveSize: DEFAULT_CONFIG.botClass,
  };
}

function buildServerSlotRows(lobby: ActiveServerLobby | null, side: 'A' | 'B'): DisplaySlotRow[] | null {
  if (!lobby) return null;
  const rows = Array.from({ length: SLOTS_PER_SIDE }, (_, i) => {
    const slotIndex = side === 'A' ? i : i + SLOTS_PER_SIDE;
    const slot = (lobby.slots ?? []).find((s) => s.index === slotIndex);
    const player = slot?.player ?? null;
    const kind: SlotKind = player
      ? (player.isBot ? 'bot' : (player.userId === lobby.ownerUserId ? 'host' : 'human'))
      : 'open';
    return {
      key: `${side.toLowerCase()}-${slotIndex}`,
      kind,
      index: i,
      player,
      status: slot?.status,
    };
  });
  return rows;
}

function loadConfig(): LobbyConfig {
  if (typeof window === 'undefined') return DEFAULT_CONFIG;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_CONFIG;
    const parsed = JSON.parse(raw) as Partial<LobbyConfig>;
    return { ...DEFAULT_CONFIG, ...parsed, slotsA: normalizeSlots(parsed.slotsA, 'host'), slotsB: normalizeSlots(parsed.slotsB) };
  } catch {
    return DEFAULT_CONFIG;
  }
}

function normalizeSlots(slots: SlotKind[] | undefined, firstAs?: SlotKind): SlotKind[] {
  const out: SlotKind[] = Array(SLOTS_PER_SIDE).fill('open');
  if (firstAs === 'host') out[0] = 'host';
  if (Array.isArray(slots)) {
    for (let i = 0; i < SLOTS_PER_SIDE; i++) {
      const s = slots[i];
      if (s === 'host') out[i] = i === 0 && firstAs === 'host' ? 'host' : 'open';
      else if (s === 'bot' || s === 'open') out[i] = s;
      else if (s === 'empty') out[i] = 'open';
    }
    if (firstAs === 'host') out[0] = 'host';
  }
  return out;
}

type LobbyBotPanelTone = 'default' | 'alt';

function LobbySideBotPanel({
  tone,
  disabled,
  difficulty,
  onDifficulty,
  onAddBots,
  onFillBots,
}: {
  tone: LobbyBotPanelTone;
  disabled: boolean;
  difficulty: DifficultyKey;
  onDifficulty: (d: DifficultyKey) => void;
  onAddBots: (n: number) => void;
  onFillBots: () => void;
}) {
  const [preset, setPreset] = useState<'assault' | 'support' | 'mixed'>('mixed');
  const [autobalance, setAutobalance] = useState(false);

  return (
    <div className={tone === 'alt' ? 'mr-lobby-bot-panel mr-lobby-bot-panel--alt' : 'mr-lobby-bot-panel'}>
      <div className="mr-lobby-bot-panel-row">
        <span className="mr-lobby-bot-panel-label">ДОБАВИТЬ БОТОВ</span>
        <div className="mr-lobby-bot-panel-actions">
          <button type="button" disabled={disabled} onClick={() => onAddBots(1)}>+1 БОТ</button>
          <button type="button" disabled={disabled} onClick={() => onAddBots(2)}>+2</button>
          <button type="button" disabled={disabled} onClick={() => onAddBots(5)}>+5</button>
          <button type="button" className="mr-lobby-bot-panel-fill" disabled={disabled} onClick={onFillBots}>ЗАПОЛНИТЬ</button>
        </div>
      </div>
      <div className="mr-lobby-bot-panel-row">
        <span className="mr-lobby-bot-panel-label">ИИ</span>
        <div className="mr-lobby-bot-panel-seg" role="group">
          {(['low', 'mid', 'high'] as DifficultyKey[]).map((k) => (
            <button key={k} type="button" aria-pressed={difficulty === k} disabled={disabled} onClick={() => onDifficulty(k)}>
              {BOT_AI_SEG_LABEL[k]}
            </button>
          ))}
        </div>
      </div>
      <div className="mr-lobby-bot-panel-row mr-lobby-bot-panel-row--dense">
        <span className="mr-lobby-bot-panel-label">ПРЕСЕТ</span>
        <div className="mr-lobby-bot-panel-seg mr-lobby-bot-panel-seg--narrow">
          {([
            { k: 'assault' as const, t: 'ШТУРМ' },
            { k: 'support' as const, t: 'ПОДДЕРЖ.' },
            { k: 'mixed' as const, t: 'СМЕШАННЫЙ' },
          ]).map(({ k, t }) => (
            <button key={k} type="button" aria-pressed={preset === k} disabled={disabled} onClick={() => setPreset(k)}>
              {t}
            </button>
          ))}
        </div>
        <label className="mr-lobby-bot-panel-ab">
          <span>АВТОБАЛАНС</span>
          <input type="checkbox" checked={autobalance} disabled={disabled} onChange={(e) => setAutobalance(e.target.checked)} />
        </label>
      </div>
    </div>
  );
}

export default function MysteryRoomCreateLobby(props: Props) {
  const [lobbyNavTab, setLobbyNavTab] = useState<'create' | 'find'>(() => props.initialLobbyNavTab ?? 'create');

  useEffect(() => {
    setLobbyNavTab(props.initialLobbyNavTab ?? 'create');
  }, [props.initialLobbyNavTab]);

  const selectLobbyNavTab = useCallback(
    (tab: 'create' | 'find') => {
      setLobbyNavTab(tab);
      props.onLobbyNavTabChange?.(tab);
    },
    [props.onLobbyNavTabChange],
  );

  const [config, setConfig] = useState<LobbyConfig>(() => loadConfig());
  /** Локальные предпочтения по слотам (роль/снаряжение), без серверного API */
  const [slotPrefs, setSlotPrefs] = useState<Record<number, { role: string; loadout: string }>>({});
  const [mapPickerOpen, setMapPickerOpen] = useState(false);
  const [invitePopOpen, setInvitePopOpen] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatExpanded, setChatExpanded] = useState(false);
  /** Лобби на сервере после успешного lobby/create */
  const [serverLobbyId, setServerLobbyId] = useState<number | null>(null);
  const [serverLobbyCode, setServerLobbyCode] = useState<string | null>(null);
  const [hostLobby, setHostLobby] = useState<ActiveServerLobby | null>(null);
  /** Серверное лобби, куда пользователь вошёл гостем из поиска */
  const [guestLobby, setGuestLobby] = useState<ActiveServerLobby | null>(null);
  /** Системные строки в чате для гостя (статусы без футера). */
  const [guestLobbySystemChat, setGuestLobbySystemChat] = useState<ChatMessage[]>([]);
  const guestWelcomeAnnouncedLobbyIdRef = useRef<number | null>(null);
  const [createLobbyBusy, setCreateLobbyBusy] = useState(false);
  const [footerActionError, setFooterActionError] = useState('');
  const chatScrollRef = useRef<HTMLDivElement | null>(null);
  const chatRef = useRef<HTMLDivElement | null>(null);
  /** Зачем: не открываем режим игры дважды (poll + повторный ready). */
  const mrMatchAutoNavScheduledRef = useRef(false);
  const guestLobbyPollTransientAnnouncedRef = useRef(false);
  const guestLobbyChatLoadTransientAnnouncedRef = useRef(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>(() => [
    { author: 'Система', text: 'Лобби создано. Готовьтесь к запуску.', ts: Date.now() - 60_000, system: true },
    { author: 'VoidWalker', text: 'Готовы к запуску.', ts: Date.now() - 30_000, mine: true },
    { author: 'Nighthawk_7', text: 'Проверка снаряжения.', ts: Date.now() - 20_000 },
    { author: 'Spectre_09', text: 'Беру тяжёлое вооружение.', ts: Date.now() - 10_000 },
  ]);
  const [serverChatMessages, setServerChatMessages] = useState<MrLobbyChatMessage[]>([]);
  const storedUser = useStoredMenuUser();
  const currentUserId = useMemo(() => readStoredUserId(), []);
  const guestMode = guestLobby !== null;
  const activeServerLobby = guestLobby ?? hostLobby;

  const botCountTotal = activeServerLobby
    ? activeServerLobby.players.filter((p) => p.isBot).length
    : config.slotsA.filter((s) => s === 'bot').length + config.slotsB.filter((s) => s === 'bot').length;
  const openSlotsTotal = activeServerLobby
    ? (activeServerLobby.slots ?? []).filter((s) => s.status === 'open' && !s.player).length
    : config.slotsA.filter((s) => s === 'open').length + config.slotsB.filter((s) => s === 'open').length;
  const botsOnly = openSlotsTotal === 0 && botCountTotal > 0;
  /** Зачем: в signaling botsOnly запрещает join — только если живой один участник и нет мест под людей. */
  const humanPlayersMr = activeServerLobby ? activeServerLobby.players.filter((p) => !p.isBot).length : 1;
  const p2pSignalingBotsOnly = botsOnly && humanPlayersMr <= 1;

  const appendGuestLobbySystem = useCallback((text: string) => {
    setGuestLobbySystemChat((prev) => [...prev, { author: 'Система', text, ts: Date.now(), system: true }]);
  }, []);

  const dismissGuestLobbyToFind = useCallback(
    (message: string) => {
      setGuestLobby(null);
      setHostLobby(null);
      setServerLobbyId(null);
      setServerLobbyCode(null);
      guestWelcomeAnnouncedLobbyIdRef.current = null;
      setGuestLobbySystemChat([]);
      setChatMessages((cur) => [...cur, { author: 'Система', text: message, ts: Date.now(), system: true }]);
      setServerChatMessages([]);
      selectLobbyNavTab('find');
    },
    [selectLobbyNavTab],
  );

  const handleBackHome = useCallback(async () => {
    const ownsLobby =
      !guestMode && activeServerLobby && currentUserId !== null && activeServerLobby.ownerUserId === currentUserId;
    if (ownsLobby) {
      try {
        await mrApiLobbyDisband({ lobbyId: activeServerLobby.id });
      } catch {
        try {
          await mrApiLobbyLeave({ lobbyId: activeServerLobby.id });
        } catch {
          // сеть недоступна — меню всё равно закроем локально
        }
      }
      setHostLobby(null);
      setServerLobbyId(null);
      setServerLobbyCode(null);
    }
    props.onBackHome();
  }, [activeServerLobby, currentUserId, guestMode, props.onBackHome]);

  const visibleChatMessages = useMemo<ChatMessage[]>(() => {
    if (!activeServerLobby) return chatMessages;
    const merged: ChatMessage[] = [
      ...guestLobbySystemChat,
      ...serverChatMessages.map((message) => ({
        author: message.displayName,
        text: message.message,
        ts: parseServerTs(message.createdAt),
        mine: currentUserId !== null && message.userId === currentUserId,
      })),
    ];
    merged.sort((a, b) => a.ts - b.ts);
    return merged;
  }, [activeServerLobby, chatMessages, currentUserId, guestLobbySystemChat, serverChatMessages]);

  const scheduleMrMatchPlayFromResponse = useCallback(
    (response: MrLobbyResponse) => {
      if (mrMatchAutoNavScheduledRef.current) return;
      const matched = response.matchId != null || response.status === 'matched';
      const synced = response.lobby;
      const code = synced?.lobbyCode?.trim();
      if (!matched || !synced || !code) return;

      const ownerStartingAsHost =
        !guestLobby && currentUserId !== null && synced.ownerUserId === currentUserId;

      mrMatchAutoNavScheduledRef.current = true;
      if (ownerStartingAsHost) {
        props.onStartLobby({
          botCount: botCountTotal,
          botsOnly: p2pSignalingBotsOnly,
          gameMode: config.gameMode,
          botDifficulty: config.difficulty,
          globalMission: config.mission,
          zombieWaveCount: config.waveCount,
          zombieWaveSize: config.botClass,
          mrLinkedLobbyCode: code,
          syncedMrLobby: synced,
        });
        return;
      }
      props.onJoinP2PLobby(code, buildMrLobbyP2pMenuOverrides(synced));
    },
    [
      botCountTotal,
      p2pSignalingBotsOnly,
      config.botClass,
      config.difficulty,
      config.gameMode,
      config.mission,
      config.waveCount,
      currentUserId,
      guestLobby,
      props.onJoinP2PLobby,
      props.onStartLobby,
    ],
  );

  useEffect(() => {
    mrMatchAutoNavScheduledRef.current = false;
  }, [activeServerLobby?.id]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    } catch {
      // настройки лобби не критичны
    }
  }, [config]);

  useEffect(() => {
    if (lobbyNavTab !== 'create' || activeServerLobby || currentUserId === null) return;
    let mounted = true;
    void mrApiLobbyCurrent()
      .then((response) => {
        if (!mounted || !response.lobby) return;
        if (response.lobby.ownerUserId === currentUserId) {
          setHostLobby(response.lobby);
          setServerLobbyId(response.lobby.id);
          setServerLobbyCode(response.lobby.lobbyCode ?? null);
          return;
        }
        if (props.currentLobbyMode === 'owned') return;
        setGuestLobby(response.lobby);
        setHostLobby(null);
        setServerLobbyId(null);
        setServerLobbyCode(null);
        if (guestWelcomeAnnouncedLobbyIdRef.current !== response.lobby.id) {
          guestWelcomeAnnouncedLobbyIdRef.current = response.lobby.id;
          setGuestLobbySystemChat([]);
          appendGuestLobbySystem('Вы в лобби хоста.');
        }
        return;
      })
      .catch(() => {
        // Активного лобби может не быть.
      });
    return () => {
      mounted = false;
    };
  }, [activeServerLobby, appendGuestLobbySystem, currentUserId, lobbyNavTab, props.currentLobbyMode]);

  useEffect(() => {
    if (!activeServerLobby?.id) return;
    let mounted = true;
    const poll = async () => {
      try {
        const response = await mrApiLobbyPoll(activeServerLobby.id);
        if (!mounted) return;
        if (response.lobby) {
          // Зачем: после старта матча в БД у лобби status=closed, но в ответе есть matched/matchId — гостя ведём в P2P, а не в «поиск».
          const matchBegun = response.matchId != null || response.status === 'matched';
          if (guestMode && response.lobby.status === 'closed' && !matchBegun) {
            dismissGuestLobbyToFind('Лобби закрыто.');
            return;
          }
          if (guestMode) setGuestLobby(response.lobby);
          else setHostLobby(response.lobby);
        }
        guestLobbyPollTransientAnnouncedRef.current = false;
        scheduleMrMatchPlayFromResponse(response);
      } catch (error: unknown) {
        if (!mounted) return;
        if (guestMode && mrIsForcedLobbyExitError(error)) {
          dismissGuestLobbyToFind(mrApiErrorMessage(error, 'Хост распустил лобби или лобби удалено.'));
          return;
        }
        const msg = mrApiErrorMessage(error, 'Лобби временно недоступно');
        if (guestMode) {
          if (!guestLobbyPollTransientAnnouncedRef.current) {
            appendGuestLobbySystem(msg);
            guestLobbyPollTransientAnnouncedRef.current = true;
          }
        } else {
          setFooterActionError(msg);
        }
      }
    };
    void poll();
    const timer = window.setInterval(() => void poll(), 2000);
    return () => {
      mounted = false;
      window.clearInterval(timer);
    };
  }, [
    activeServerLobby?.id,
    appendGuestLobbySystem,
    dismissGuestLobbyToFind,
    guestMode,
    scheduleMrMatchPlayFromResponse,
  ]);

  useEffect(() => {
    if (!activeServerLobby?.id) {
      setServerChatMessages([]);
      return;
    }
    let mounted = true;
    const loadChat = async () => {
      try {
        const response = await mrApiLobbyChatList(activeServerLobby.id);
        if (mounted) {
          guestLobbyChatLoadTransientAnnouncedRef.current = false;
          setServerChatMessages(response.messages ?? []);
        }
      } catch (error: unknown) {
        if (mounted && guestMode && mrIsForcedLobbyExitError(error)) {
          dismissGuestLobbyToFind(mrApiErrorMessage(error, 'Хост распустил лобби или лобби удалено.'));
          return;
        }
        if (mounted) {
          const msg = mrApiErrorMessage(error, 'Не удалось обновить чат лобби');
          if (guestMode) {
            if (!guestLobbyChatLoadTransientAnnouncedRef.current) {
              appendGuestLobbySystem(msg);
              guestLobbyChatLoadTransientAnnouncedRef.current = true;
            }
          } else {
            setFooterActionError(msg);
          }
        }
      }
    };
    void loadChat();
    const timer = window.setInterval(() => void loadChat(), 2000);
    return () => {
      mounted = false;
      window.clearInterval(timer);
    };
  }, [activeServerLobby?.id, appendGuestLobbySystem, dismissGuestLobbyToFind, guestMode]);

  const enterGuestLobby = useCallback(
    (lobby: ActiveServerLobby) => {
      setGuestLobby(lobby);
      setHostLobby(null);
      setServerLobbyId(null);
      setServerLobbyCode(null);
      guestWelcomeAnnouncedLobbyIdRef.current = lobby.id;
      setGuestLobbySystemChat([]);
      setMapPickerOpen(false);
      setInvitePopOpen(false);
      setFooterActionError('');
      appendGuestLobbySystem(`Вы в лобби хоста (#${lobby.id}). Доступны готовность, чат и выход.`);
      selectLobbyNavTab('create');
      void mrApiLobbyPoll(lobby.id).then(
        (snapshot) => {
          scheduleMrMatchPlayFromResponse(snapshot);
        },
        (error: unknown) => {
          if (mrIsForcedLobbyExitError(error)) {
            dismissGuestLobbyToFind(mrApiErrorMessage(error, 'Лобби больше не существует.'));
          }
        },
      );
    },
    [appendGuestLobbySystem, dismissGuestLobbyToFind, scheduleMrMatchPlayFromResponse, selectLobbyNavTab],
  );

  // Esc для возврата
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (chatExpanded) setChatExpanded(false);
      else if (mapPickerOpen) setMapPickerOpen(false);
      else if (invitePopOpen) setInvitePopOpen(false);
      else if (lobbyNavTab === 'find') selectLobbyNavTab('create');
      else handleBackHome();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [chatExpanded, handleBackHome, invitePopOpen, lobbyNavTab, mapPickerOpen, selectLobbyNavTab]);

  // Клик мимо чата — сворачивание
  useEffect(() => {
    if (!chatExpanded) return;
    const handler = (e: MouseEvent) => {
      if (chatRef.current && !chatRef.current.contains(e.target as Node)) {
        setChatExpanded(false);
      }
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [chatExpanded]);

  const profileName = useMemo(() => resolveMenuUserName(storedUser, props.profile), [storedUser, props.profile]);
  const profileAvatar = useMemo(() => resolveMenuAvatar(storedUser), [storedUser]);
  const profileLevel = props.profile?.rating ?? 41;
  const moneyLabel = props.profile ? formatCompact(props.profile.money) : '125 670';
  const ratingLabel = props.profile ? formatCompact(props.profile.rating) : '2 340';

  const sortedFriends = useMemo(() => sortMysteryRoomFriends(props.friends), [props.friends]);
  const onlineFriends = useMemo(() => sortedFriends.filter((f) => f.is_online || isMysteryRoomFriendPlaying(f)), [sortedFriends]);

  const allMaps = useMemo<MapPickerItem[]>(() => {
    const list: MapPickerItem[] = getBuiltinMysteryRoomMapDefinitions().map((map) => ({
      id: map.id,
      title: map.title,
      subtitle: `Встроенная · ${formatMysteryRoomPlayableModesShort(map)}`,
      kind: 'builtin',
    }));
    for (const m of props.publishedMaps) {
      list.push({
        id: m.id,
        title: m.title,
        subtitle: `${m.ownerName || `user #${m.ownerUserId}`} · ${formatMysteryRoomPlayableModesShort(m)}`,
        kind: 'published',
        source: m,
      });
    }
    for (const m of props.myMaps) {
      if (m.status !== 'published') continue;
      if (list.some((x) => x.id === m.id)) continue;
      list.push({
        id: m.id,
        title: m.title,
        subtitle: `Моя карта · ${formatMysteryRoomPlayableModesShort(m)}`,
        kind: 'my',
        source: m,
      });
    }
    return list;
  }, [props.publishedMaps, props.myMaps]);

  const displayConfig = useMemo(() => mergeLobbyConfigFromServer(guestLobby, config), [config, guestLobby]);
  const displayMapId = activeServerLobby?.mapId ?? props.selectedMapId;
  const displayMapDefinition = useMemo(
    () => findMysteryRoomMapDefinition(displayMapId) ?? props.selectedMapDefinition,
    [displayMapId, props.selectedMapDefinition],
  );

  const mapsForCurrentMode = useMemo(
    () =>
      allMaps.filter((m) => {
        if (m.kind === 'builtin') {
          const d = findMysteryRoomMapDefinition(m.id);
          return d ? mysteryRoomMapSupportsGameMode(d, displayConfig.gameMode) : true;
        }
        return m.source ? mysteryRoomMapSupportsGameMode(m.source, displayConfig.gameMode) : true;
      }),
    [allMaps, displayConfig.gameMode],
  );

  const selectedMapItem = useMemo(() => allMaps.find((m) => m.id === displayMapId) ?? allMaps[0], [allMaps, displayMapId]);
  const selectedMapMatchesMode = useMemo(
    () => mysteryRoomMapSupportsGameMode(displayMapDefinition, displayConfig.gameMode),
    [displayMapDefinition, displayConfig.gameMode],
  );
  const mapDescription = useMemo(
    () => buildMapDescription(displayMapId, displayMapDefinition, selectedMapItem?.source),
    [displayMapId, displayMapDefinition, selectedMapItem],
  );

  const seatedInSlotsCount = activeServerLobby
    ? activeServerLobby.players.filter((p) => p.slotIndex !== null && p.slotIndex !== undefined).length
    : config.slotsA.filter((s) => s === 'bot').length + config.slotsB.filter((s) => s === 'bot').length + 1;
  const rosterNonClosedCount = (activeServerLobby?.slots ?? []).filter((s) => s.status !== 'closed').length;
  const rosterTargetSlots =
    activeServerLobby?.requiredPlayers ??
    (rosterNonClosedCount > 0 ? rosterNonClosedCount : activeServerLobby?.settings?.maxPlayers ?? activeServerLobby?.slots?.length ?? 10);
  const serverMaxPlayers = rosterTargetSlots;
  const playerCountLabel = activeServerLobby
    ? `${seatedInSlotsCount}/${serverMaxPlayers}`
    : `${botCountTotal + openSlotsTotal + 1}/10`;
  const lobbyRegisteredOnServer = Boolean(!guestLobby && (serverLobbyId !== null || hostLobby !== null));
  const guestPlayer = useMemo(() => guestLobby?.players.find((p) => p.userId === currentUserId) ?? null, [currentUserId, guestLobby?.players]);
  const guestReady = Boolean(guestPlayer?.isReady);
  const guestHasSlot = guestPlayer?.slotIndex !== null && guestPlayer?.slotIndex !== undefined;
  const unassignedPlayers = activeServerLobby?.unassignedPlayers ?? [];
  const displaySlotsA = useMemo(
    (): DisplaySlotRow[] => buildServerSlotRows(activeServerLobby, 'A') ?? config.slotsA.map((kind, index) => ({ key: `a-${index}`, kind, index })),
    [activeServerLobby, config.slotsA],
  );
  const displaySlotsB = useMemo(
    (): DisplaySlotRow[] => buildServerSlotRows(activeServerLobby, 'B') ?? config.slotsB.map((kind, index) => ({ key: `b-${index}`, kind, index })),
    [activeServerLobby, config.slotsB],
  );

  const registerLobbyOnServer = useCallback(async () => {
    if (props.mapsAuthRequired) {
      setFooterActionError('Войдите в аккаунт, чтобы записать лобби в базу.');
      return;
    }
    setCreateLobbyBusy(true);
    setFooterActionError('');
    try {
      const maxPlayers = SLOTS_PER_SIDE * 2;
      const res = await mrApiLobbyCreate({
        isPrivate: config.lobbyType === 'private',
        mapId: props.selectedMapId,
        gameMode: mapLobbyGameModeForApi(config.gameMode),
        botDifficulty: mapBotDifficultyForApi(config.difficulty),
        incidentLevel: mapIncidentForApi(config.zombieLevel),
        maxPlayers,
      });
      const lobby = res.lobby;
      if (!lobby?.id) {
        setFooterActionError('Сервер не вернул данные лобби.');
        return;
      }
      if (currentUserId !== null && lobby.ownerUserId !== currentUserId) {
        setFooterActionError('Сервер вернул чужое лобби. Выйдите из чужого лобби и создайте своё заново.');
        return;
      }
      let syncedLobby = lobby;
      const configuredSlots = [...config.slotsA, ...config.slotsB];
      for (let slotIndex = 1; slotIndex < configuredSlots.length; slotIndex += 1) {
        if (configuredSlots[slotIndex] !== 'bot') continue;
        const slotResponse = await mrApiLobbySlot({
          lobbyId: lobby.id,
          slotIndex,
          action: 'bot',
          botDifficulty: mapBotDifficultyForApi(config.difficulty),
        });
        if (slotResponse.lobby) {
          syncedLobby = slotResponse.lobby;
        }
      }
      setServerLobbyId(syncedLobby.id);
      setServerLobbyCode(syncedLobby.lobbyCode ?? null);
      setHostLobby(syncedLobby);
      const codeLine = lobby.lobbyCode ? `Код: ${lobby.lobbyCode}` : 'Код будет доступен в профиле лобби';
      setChatMessages((cur) => [...cur, { author: 'Система', text: `Лобби создано в базе (#${syncedLobby.id}). ${codeLine}`, ts: Date.now(), system: true }]);
    } catch (err: unknown) {
      setFooterActionError(mrApiErrorMessage(err, 'Не удалось создать лобби'));
    } finally {
      setCreateLobbyBusy(false);
    }
  }, [
    botCountTotal,
    config.difficulty,
    config.gameMode,
    config.lobbyType,
    config.zombieLevel,
    currentUserId,
    openSlotsTotal,
    props.mapsAuthRequired,
    props.selectedMapId,
  ]);

  useEffect(() => {
    if (!serverLobbyId || guestMode) return;
    const timer = window.setTimeout(() => {
      void mrApiLobbySettings({
        lobbyId: serverLobbyId,
        mapId: props.selectedMapId,
        gameMode: mapLobbyGameModeForApi(config.gameMode),
        botDifficulty: mapBotDifficultyForApi(config.difficulty),
        incidentLevel: mapIncidentForApi(config.zombieLevel),
        maxPlayers: SLOTS_PER_SIDE * 2,
      })
        .then((response) => {
          if (response.lobby) setHostLobby(response.lobby);
        })
        .catch((error: any) => {
          setFooterActionError(mrApiErrorMessage(error, 'Не удалось обновить настройки лобби'));
        });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [
    config.difficulty,
    config.gameMode,
    config.zombieLevel,
    guestMode,
    props.selectedMapId,
    serverLobbyId,
  ]);

  const syncHostSlot = useCallback(
    async (slotIndex: number, next: SlotKind) => {
      if (!serverLobbyId || guestMode || next === 'host' || next === 'human') return;
      const action = next === 'bot' ? 'bot' : 'open';
      try {
        const response = await mrApiLobbySlot({
          lobbyId: serverLobbyId,
          slotIndex,
          action,
          botDifficulty: mapBotDifficultyForApi(config.difficulty),
        });
        if (response.lobby) setHostLobby(response.lobby);
      } catch (error: any) {
        setFooterActionError(mrApiErrorMessage(error, 'Не удалось обновить слот лобби'));
      }
    },
    [config.difficulty, guestMode, serverLobbyId],
  );

  const cycleSlot = (side: 'A' | 'B', idx: number) => {
    if (guestMode) return;
    const key = side === 'A' ? 'slotsA' : 'slotsB';
    const otherKey = side === 'A' ? 'slotsB' : 'slotsA';
    const slots = [...config[key]];
    const cur0 = slots[idx];
    if (cur0 === 'host') return;
    const botsElse =
      config[otherKey].filter((s) => s === 'bot').length + slots.filter((s, j) => j !== idx && s === 'bot').length;
    const next: SlotKind =
      cur0 === 'open' ? (botsElse >= MYSTERY_ROOM_MAX_WANDER_BOTS ? 'open' : 'bot') : cur0 === 'bot' ? 'open' : cur0;
    slots[idx] = next;
    setConfig((cur) => ({ ...cur, [key]: slots }));
    const slotIndex = side === 'A' ? idx : idx + SLOTS_PER_SIDE;
    void syncHostSlot(slotIndex, next);
  };

  const fillBots = (side: 'A' | 'B') => {
    if (guestMode) return;
    const key = side === 'A' ? 'slotsA' : 'slotsB';
    setConfig((cur) => {
      let botsAvailable =
        MYSTERY_ROOM_MAX_WANDER_BOTS -
        cur.slotsA.filter((s) => s === 'bot').length -
        cur.slotsB.filter((s) => s === 'bot').length;
      const changed: number[] = [];
      const slots = cur[key].map((s, i) => {
        if (side === 'A' && i === 0) return s;
        if (s !== 'open' || botsAvailable <= 0) return s;
        botsAvailable--;
        changed.push(side === 'A' ? i : i + SLOTS_PER_SIDE);
        return 'bot';
      });
      queueMicrotask(() => {
        for (const slotIndex of changed) void syncHostSlot(slotIndex, 'bot');
      });
      return { ...cur, [key]: slots };
    });
  };

  const addBotsToSide = (side: 'A' | 'B', n: number) => {
    if (guestMode || n <= 0) return;
    const key = side === 'A' ? 'slotsA' : 'slotsB';
    setConfig((cur) => {
      let left = Math.min(
        n,
        MYSTERY_ROOM_MAX_WANDER_BOTS -
          cur.slotsA.filter((s) => s === 'bot').length -
          cur.slotsB.filter((s) => s === 'bot').length,
      );
      const changed: number[] = [];
      const slots = [...cur[key]];
      for (let i = 0; i < slots.length && left > 0; i++) {
        if (side === 'A' && i === 0) continue;
        if (slots[i] !== 'open') continue;
        slots[i] = 'bot';
        changed.push(side === 'A' ? i : i + SLOTS_PER_SIDE);
        left--;
      }
      queueMicrotask(() => {
        for (const slotIndex of changed) void syncHostSlot(slotIndex, 'bot');
      });
      return { ...cur, [key]: slots };
    });
  };

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text) return;
    setChatInput('');
    if (activeServerLobby?.id) {
      try {
        const response = await mrApiLobbyChatSend({ lobbyId: activeServerLobby.id, message: text });
        setServerChatMessages(response.messages ?? []);
      } catch (error: unknown) {
        if (guestMode && mrIsForcedLobbyExitError(error)) {
          dismissGuestLobbyToFind(mrApiErrorMessage(error, 'Хост распустил лобби или лобби удалено.'));
          return;
        }
        const msg = mrApiErrorMessage(error, 'Не удалось отправить сообщение');
        if (guestMode) appendGuestLobbySystem(msg);
        else setFooterActionError(msg);
      }
      return;
    }
    setChatMessages((cur) => [...cur, { author: profileName, text, ts: Date.now(), mine: true, avatarUrl: profileAvatar }]);
  };

  // Автоскролл вниз при новом сообщении
  useEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [visibleChatMessages]);

  const commitRegisteredHostPlay = useCallback(async () => {
    if (guestLobby) return;
    if (mrMatchAutoNavScheduledRef.current) return;
    const lobbyIdForReady = serverLobbyId ?? hostLobby?.id ?? null;
    if (lobbyIdForReady === null) return;

    setFooterActionError('');
    setCreateLobbyBusy(true);
    try {
      const readyRes = await mrApiLobbyReady({ lobbyId: lobbyIdForReady, ready: true });
      if (!readyRes.matchId) {
        setFooterActionError('Не все приглашённые игроки готовы или условия старта не выполнены.');
        return;
      }
      const code =
        readyRes.lobby?.lobbyCode?.trim() ||
        hostLobby?.lobbyCode?.trim() ||
        serverLobbyCode?.trim() ||
        '';
      if (!code) {
        setFooterActionError('Сервер не вернул код лобби для P2P.');
        return;
      }
      mrMatchAutoNavScheduledRef.current = true;
      props.onStartLobby({
        botCount: botCountTotal,
        botsOnly: p2pSignalingBotsOnly,
        gameMode: config.gameMode,
        botDifficulty: config.difficulty,
        globalMission: config.mission,
        zombieWaveCount: config.waveCount,
        zombieWaveSize: config.botClass,
        mrLinkedLobbyCode: code,
        syncedMrLobby: readyRes.lobby ?? undefined,
      });
    } catch (error: unknown) {
      const fallback = 'Не удалось запустить лобби';
      const raw = error instanceof Error ? error.message : '';
      const networkHint =
        (error instanceof TypeError && raw.includes('fetch')) ||
        /^failed to fetch$/i.test(raw.trim());
      setFooterActionError(
        networkHint ? 'Нет связи с сервером или запрос был прерван. Проверьте сеть и повторите.' : mrApiErrorMessage(error, fallback),
      );
    } finally {
      setCreateLobbyBusy(false);
    }
  }, [
    botCountTotal,
    p2pSignalingBotsOnly,
    config.botClass,
    config.difficulty,
    config.gameMode,
    config.mission,
    config.waveCount,
    guestLobby,
    hostLobby?.id,
    hostLobby?.lobbyCode,
    props.onStartLobby,
    serverLobbyCode,
    serverLobbyId,
  ]);

  const handleFooterPrimary = useCallback(async () => {
    setFooterActionError('');
    if (guestLobby) {
      if (!guestHasSlot) {
        appendGuestLobbySystem('Сначала займите слот. Пока вы в комнате ожидания.');
        return;
      }
      try {
        const response = await mrApiLobbyReady({ lobbyId: guestLobby.id, ready: !guestReady });
        if (response.lobby) {
          setGuestLobby(response.lobby);
        }
        scheduleMrMatchPlayFromResponse(response);
        appendGuestLobbySystem(!guestReady ? 'Готовность подтверждена.' : 'Готовность снята.');
      } catch (error: unknown) {
        if (mrIsForcedLobbyExitError(error)) {
          dismissGuestLobbyToFind(mrApiErrorMessage(error, 'Хост распустил лобби или лобби удалено.'));
          return;
        }
        appendGuestLobbySystem(mrApiErrorMessage(error, 'Не удалось изменить готовность'));
      }
      return;
    }
    if (!guestLobby && serverLobbyId === null && hostLobby === null) {
      await registerLobbyOnServer();
      return;
    }
    await commitRegisteredHostPlay();
  }, [
    appendGuestLobbySystem,
    commitRegisteredHostPlay,
    dismissGuestLobbyToFind,
    guestHasSlot,
    guestLobby,
    guestReady,
    hostLobby,
    registerLobbyOnServer,
    scheduleMrMatchPlayFromResponse,
    serverLobbyId,
  ]);

  const leaveGuestLobby = useCallback(async () => {
    if (!guestLobby) return;
    try {
      await mrApiLobbyLeave({ lobbyId: guestLobby.id });
      setGuestLobby(null);
      guestWelcomeAnnouncedLobbyIdRef.current = null;
      setGuestLobbySystemChat([]);
      guestLobbyPollTransientAnnouncedRef.current = false;
      guestLobbyChatLoadTransientAnnouncedRef.current = false;
      setChatMessages((cur) => [...cur, { author: 'Система', text: 'Вы вышли из лобби.', ts: Date.now(), system: true }]);
    } catch (error: unknown) {
      if (mrIsForcedLobbyExitError(error)) {
        dismissGuestLobbyToFind(mrApiErrorMessage(error, 'Вы больше не в этом лобби.'));
        return;
      }
      appendGuestLobbySystem(mrApiErrorMessage(error, 'Не удалось выйти из лобби'));
    }
  }, [appendGuestLobbySystem, dismissGuestLobbyToFind, guestLobby]);

  const takeGuestSlot = useCallback(
    async (slotIndex: number) => {
      if (!guestLobby) return;
      try {
        const response = await mrApiLobbySlot({ lobbyId: guestLobby.id, slotIndex, action: 'take' });
        if (response.lobby) setGuestLobby(response.lobby);
        appendGuestLobbySystem(`Слот #${slotIndex + 1} занят.`);
      } catch (error: unknown) {
        if (mrIsForcedLobbyExitError(error)) {
          dismissGuestLobbyToFind(mrApiErrorMessage(error, 'Хост распустил лобби или лобби удалено.'));
          return;
        }
        appendGuestLobbySystem(mrApiErrorMessage(error, 'Не удалось занять слот'));
      }
    },
    [appendGuestLobbySystem, dismissGuestLobbyToFind, guestLobby],
  );

  const releaseGuestSlot = useCallback(async () => {
    if (!guestLobby) return;
    try {
      const response = await mrApiLobbySlot({ lobbyId: guestLobby.id, action: 'leave' });
      if (response.lobby) setGuestLobby(response.lobby);
      appendGuestLobbySystem('Вы перешли в комнату ожидания.');
    } catch (error: unknown) {
      if (mrIsForcedLobbyExitError(error)) {
        dismissGuestLobbyToFind(mrApiErrorMessage(error, 'Хост распустил лобби или лобби удалено.'));
        return;
      }
      appendGuestLobbySystem(mrApiErrorMessage(error, 'Не удалось освободить слот'));
    }
  }, [appendGuestLobbySystem, dismissGuestLobbyToFind, guestLobby]);

  const copyLobbyCode = async () => {
    const text = guestLobby?.lobbyCode?.trim() || serverLobbyCode?.trim() || props.matchId?.trim();
    if (!text) return;
    try {
      await navigator.clipboard?.writeText(text);
    } catch {
      // буфер недоступен — игнор
    }
  };

  const lobbyCode = guestLobby?.lobbyCode?.trim() || serverLobbyCode?.trim() || props.matchId?.trim() || '—';

  return (
    <div className="mr-lobby-shell">
      <header className="mr-ref-topbar" style={{ position: 'relative', zIndex: 6 }}>
        <button type="button" onClick={handleBackHome} className="mr-ref-logo-cell" aria-label="Главное меню">
          <span className="mr-ref-logo-mark" aria-hidden="true" />
        </button>
        <nav className="mr-ref-nav" aria-label="Разделы лобби">
          <button
            type="button"
            onClick={handleBackHome}
            className="mr-ref-nav-item mr-ref-nav-item--home"
            title="Вернуться в главное меню"
          >
            ← ГЛАВНОЕ МЕНЮ
          </button>
          <button
            type="button"
            className={`mr-ref-nav-item ${lobbyNavTab === 'create' ? 'active' : ''}`}
            aria-current={lobbyNavTab === 'create' ? 'page' : undefined}
            onClick={() => selectLobbyNavTab('create')}
          >
            СОЗДАНИЕ ЛОББИ
          </button>
          <button
            type="button"
            className={`mr-ref-nav-item ${lobbyNavTab === 'find' ? 'active' : ''}`}
            aria-current={lobbyNavTab === 'find' ? 'page' : undefined}
            onClick={() => selectLobbyNavTab('find')}
          >
            ПОИСК ИГРЫ
          </button>
        </nav>
        <div className="mr-ref-topbar-tail">
          {lobbyNavTab === 'create' ? (
            <div className="mr-lobby-header-metrics" aria-label="Сеть и статус лобби">
              <div className="mr-lobby-hm-chip">
                <span className="mr-lobby-hm-label">РЕГИОН</span>
                <span className="mr-lobby-hm-value">🌐 Европа</span>
              </div>
              <div className="mr-lobby-hm-chip">
                <span className="mr-lobby-hm-label">PING</span>
                <span className="mr-lobby-hm-value mr-lobby-hm-value--ping">▮▮▮▯ 42 ms</span>
              </div>
              <div className="mr-lobby-hm-chip mr-lobby-hm-chip--grow">
                <span className="mr-lobby-hm-label">СТАТУС</span>
                <span className="mr-lobby-hm-value mr-lobby-hm-value--muted">
                  ⌛ {playerCountLabel} · {botsOnly ? 'Только боты' : 'Ожидание игроков'}
                </span>
              </div>
            </div>
          ) : null}
          <div className="mr-ref-top-actions">
            <span className="mr-ref-currency"><span className="mr-ref-currency-coin" aria-hidden="true" />{moneyLabel}</span>
            <span className="mr-ref-currency"><span className="mr-ref-currency-crystal" aria-hidden="true" />{ratingLabel}</span>
            <button type="button" onClick={props.onOpenAbout} className="mr-ref-top-icon" title="Почта">✉</button>
            <button type="button" onClick={() => setInvitePopOpen((v) => !v)} className="mr-ref-top-icon" title="Друзья">👥</button>
            <button type="button" onClick={handleBackHome} className="mr-ref-top-icon" title="Уведомления">🔔</button>
            <button type="button" onClick={props.onOpenEditor} className="mr-ref-top-icon" title="Настройки">⚙</button>
          </div>
        </div>
      </header>

      <div className="mr-lobby-stage">
      {lobbyNavTab === 'find' ? (
        <div className="mr-ref-overlay mr-ref-overlay--lobby" role="dialog" aria-modal="true" aria-label="Поиск игры">
          <div className="mr-ref-overlay-head">
            <span className="mr-ref-overlay-title">ПОИСК ИГРЫ</span>
            <button type="button" className="mr-ref-overlay-close" aria-label="Закрыть поиск" onClick={() => selectLobbyNavTab('create')}>
              ×
            </button>
          </div>
          <div className="mr-ref-overlay-body flex min-h-0 flex-1 flex-col">
            <MysteryRoomJoinBrowser
              embedded
              onOpenCreateLobby={() => selectLobbyNavTab('create')}
              onJoinedLobby={enterGuestLobby}
            />
          </div>
        </div>
      ) : (
        <>
      <div className="mr-lobby-content">
        {/* Сторона А */}
        <aside className="mr-lobby-side" aria-label="Сторона А">
          <div className="mr-lobby-side-head">СТОРОНА А</div>
          <LobbySideBotPanel
            tone="default"
            disabled={guestMode}
            difficulty={displayConfig.difficulty}
            onDifficulty={(d) => setConfig((c) => ({ ...c, difficulty: d }))}
            onAddBots={(n) => addBotsToSide('A', n)}
            onFillBots={() => fillBots('A')}
          />
          {displaySlotsA.map((slot) => (
            <SlotRow
              key={slot.key}
              kind={slot.kind}
              index={slot.index}
              hostName={slot.player?.displayName ?? profileName}
              hostAvatar={profileAvatar}
              hostLevel={profileLevel}
              player={slot.player}
              lobbyOwnerUserId={activeServerLobby?.ownerUserId ?? currentUserId}
              viewerUserId={currentUserId}
              prefRole={slotPrefs[slot.index]?.role ?? SLOT_ROLE_OPTIONS[0]}
              prefLoadout={slotPrefs[slot.index]?.loadout ?? SLOT_LOADOUT_OPTIONS[0]}
              onPrefChange={(patch) =>
                setSlotPrefs((prev) => ({
                  ...prev,
                  [slot.index]: {
                    role: SLOT_ROLE_OPTIONS[0],
                    loadout: SLOT_LOADOUT_OPTIONS[0],
                    ...prev[slot.index],
                    ...patch,
                  },
                }))
              }
              disabled={guestMode ? Boolean(slot.player || slot.status === 'closed') : Boolean(slot.player && !slot.player.isBot)}
              onCycle={() => {
                if (guestMode) void takeGuestSlot(slot.index);
                else cycleSlot('A', slot.index);
              }}
            />
          ))}
          {guestMode ? (
            <div className="mr-lobby-panel" style={{ padding: 10 }}>
              <div className="mr-lobby-panel-head" style={{ margin: '-10px -10px 8px' }}>
                <span className="mr-lobby-panel-head-icon">↪</span> ВЫ В ЛОББИ
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                <div style={{ fontSize: '0.74rem', color: 'rgba(180,192,206,0.78)', lineHeight: 1.45 }}>
                  Хост управляет картой, ботами и настройками. Вам доступны чат, выбор слота, готовность и выход.
                </div>
                {guestHasSlot ? (
                  <button
                    type="button"
                    onClick={() => void releaseGuestSlot()}
                    style={{
                      height: 32,
                      padding: '0 12px',
                      border: '1px solid rgba(251,191,36,0.38)',
                      background: 'rgba(120,79,20,0.2)',
                      color: '#fde68a',
                      fontSize: '0.74rem',
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      cursor: 'pointer',
                    }}
                  >
                    В КОМНАТУ ОЖИДАНИЯ
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => void leaveGuestLobby()}
                  style={{
                    height: 32,
                    padding: '0 12px',
                    border: '1px solid rgba(251,113,133,0.38)',
                    background: 'rgba(127,29,29,0.24)',
                    color: '#fecdd3',
                    fontSize: '0.74rem',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    cursor: 'pointer',
                  }}
                >
                  ВЫЙТИ ИЗ ЛОББИ
                </button>
              </div>
            </div>
          ) : null}
        </aside>

        {/* Центр */}
        <section className="mr-lobby-center" aria-label="Параметры матча">
          <div className="mr-lobby-center-scroll">
          <div className="mr-lobby-panel mr-lobby-panel--accent">
            <div className="mr-lobby-panel-head"><span className="mr-lobby-panel-head-icon">▣</span> ВЫБОР КАРТЫ</div>
            <div className="mr-lobby-map-card">
              <div className="mr-lobby-map-preview" aria-label={`Превью карты ${selectedMapItem?.title}`}>
                <span className="mr-lobby-map-preview-label">{selectedMapItem?.title || props.selectedMapTitle}</span>
              </div>
              <div className="mr-lobby-map-select">
                <button
                  type="button"
                  className="mr-lobby-map-select-btn"
                  disabled={guestMode}
                  onClick={() => setMapPickerOpen((v) => !v)}
                  aria-haspopup="listbox"
                  aria-expanded={mapPickerOpen}
                >
                  <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedMapItem?.title || props.selectedMapTitle}
                  </span>
                </button>
                {mapPickerOpen && (
                  <div role="listbox" className="mr-lobby-map-select-list">
                    {mapsForCurrentMode.length < allMaps.length && (
                      <div style={{ padding: '6px 12px 2px', fontSize: '0.68rem', color: 'rgba(160,172,190,0.75)' }}>
                        Показаны карты для режима «{GAME_MODE_OPTIONS.find((o) => o.key === displayConfig.gameMode)?.title ?? displayConfig.gameMode}» ({mapsForCurrentMode.length} из {allMaps.length}).
                      </div>
                    )}
                    {mapsForCurrentMode.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        role="option"
                        aria-selected={m.id === displayMapId}
                        className="mr-lobby-map-select-item"
                        onClick={() => {
                          if (m.id === BUILTIN_MAP_ID) props.onSelectBuiltin();
                          else props.onSelectCustomMap(m.id);
                          setMapPickerOpen(false);
                        }}
                      >
                        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.title}</span>
                        <small>{m.subtitle}</small>
                      </button>
                    ))}
                    {props.mapsAuthRequired && (
                      <div style={{ padding: '8px 12px', fontSize: '0.74rem', color: 'rgba(180,192,206,0.7)' }}>
                        Войдите, чтобы видеть карты сообщества.
                      </div>
                    )}
                  </div>
                )}
                <p className="mr-lobby-map-desc">{mapDescription}</p>
                {!selectedMapMatchesMode && (
                  <p
                    style={{
                      marginTop: 6,
                      fontSize: '0.7rem',
                      lineHeight: 1.45,
                      color: 'rgba(251,191,36,0.95)',
                    }}
                  >
                    Текущая карта не поддерживает режим «{GAME_MODE_OPTIONS.find((o) => o.key === displayConfig.gameMode)?.title ?? displayConfig.gameMode}».
                    Выберите карту из списка ниже.
                  </p>
                )}
                <div className="mr-lobby-map-meta">Рекомендуемый уровень: 20+</div>
                <button type="button" onClick={props.onOpenCatalog} disabled={guestMode} className="mr-ref-outline-button" style={{ marginTop: 4 }}>
                  ОТКРЫТЬ КАТАЛОГ КАРТ
                </button>
              </div>
            </div>
          </div>

          <div className="mr-lobby-panel">
            <div className="mr-lobby-panel-head"><span className="mr-lobby-panel-head-icon">⛨</span> РЕЖИМ ИГРЫ</div>
            <div className="mr-lobby-grid-4">
              {GAME_MODE_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  className="mr-lobby-option"
                  aria-pressed={displayConfig.gameMode === opt.key}
                  disabled={guestMode}
                  onClick={() => setConfig((c) => ({ ...c, gameMode: opt.key }))}
                >
                  <span className="mr-lobby-option-icon" aria-hidden="true">{opt.icon}</span>
                  <span className="mr-lobby-option-title">{opt.title}</span>
                  <span className="mr-lobby-option-desc">{opt.desc}</span>
                </button>
              ))}
            </div>
            {displayConfig.gameMode === 'infection' && (
              <p className="mr-lobby-mode-hint" style={{ margin: '8px 0 0', fontSize: '0.72rem', lineHeight: 1.45, color: 'rgba(180,192,206,0.82)' }}>
                Противостояние PvE/PvP: заражённые против операторов. Стороны A/B в лобби — состав смены; боты заполняют пустые ячейки до лимита блуждающих NPC.
              </p>
            )}
          </div>

          <div className="mr-lobby-panel">
            <div className="mr-lobby-panel-head"><span className="mr-lobby-panel-head-icon">☣</span> ГЛОБАЛЬНАЯ МИССИЯ</div>
            <div className="mr-lobby-grid-3">
              {MISSION_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  className="mr-lobby-option"
                  aria-pressed={displayConfig.mission === opt.key}
                  disabled={guestMode}
                  onClick={() => setConfig((c) => ({ ...c, mission: opt.key }))}
                >
                  <span className="mr-lobby-option-icon" aria-hidden="true">{opt.icon}</span>
                  <span className="mr-lobby-option-title">{opt.title}</span>
                  <span className="mr-lobby-option-desc">{opt.desc}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="mr-lobby-panel">
            <div className="mr-lobby-panel-head"><span className="mr-lobby-panel-head-icon">≡</span> НАЧАЛЬНЫЕ ОПЦИИ</div>
            <div className="mr-lobby-kv-grid">
              <KVRow label={displayConfig.gameMode === 'infection' ? 'Размер волны зомби' : 'Игровой класс ботов'}>
                <Stepper
                  value={displayConfig.botClass}
                  min={1}
                  max={20}
                  display={
                    displayConfig.gameMode === 'infection'
                      ? `${displayConfig.botClass} ЗОМБИ/ВОЛНА`
                      : `${displayConfig.botClass} (${Math.min(displayConfig.botClass, 5)}v${Math.min(displayConfig.botClass, 5)})`
                  }
                  disabled={guestMode}
                  onChange={(v) => setConfig((c) => ({ ...c, botClass: v }))}
                />
              </KVRow>
              <KVRow label="Сложность">
                <CycleStepper
                  options={['low', 'mid', 'high'] as DifficultyKey[]}
                  value={displayConfig.difficulty}
                  display={DIFFICULTY_LABEL[displayConfig.difficulty]}
                  disabled={guestMode}
                  onChange={(v) => setConfig((c) => ({ ...c, difficulty: v }))}
                />
              </KVRow>
              <KVRow label="Правила">
                <span style={{ fontSize: '0.74rem', color: 'rgba(160,172,190,0.62)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  🔒 Не установлены
                </span>
              </KVRow>
              <KVRow label="Макс. время матча">
                <Stepper
                  value={displayConfig.maxMatchTimeMin}
                  min={5}
                  max={120}
                  step={5}
                  display={`${displayConfig.maxMatchTimeMin} МИН`}
                  disabled={guestMode}
                  onChange={(v) => setConfig((c) => ({ ...c, maxMatchTimeMin: v }))}
                />
              </KVRow>
              <KVRow label={displayConfig.gameMode === 'infection' ? 'Количество волн' : 'Волны (режим зомби)'}>
                <Stepper
                  value={displayConfig.waveCount}
                  min={1}
                  max={30}
                  display={`${displayConfig.waveCount} ВОЛН`}
                  disabled={guestMode}
                  onChange={(v) => setConfig((c) => ({ ...c, waveCount: v }))}
                />
              </KVRow>
            </div>
          </div>

          <div className="mr-lobby-panel">
            <div className="mr-lobby-panel-head"><span className="mr-lobby-panel-head-icon">⚙</span> НАСТРОЙКИ</div>
            <div className="mr-lobby-kv-grid">
              <KVRow label="Дружественный огонь">
                <div className="mr-lobby-toggle" role="group">
                  <button type="button" aria-pressed={!displayConfig.friendlyFire} disabled={guestMode} onClick={() => setConfig((c) => ({ ...c, friendlyFire: false }))}>ВЫКЛ</button>
                  <button type="button" aria-pressed={displayConfig.friendlyFire} disabled={guestMode} onClick={() => setConfig((c) => ({ ...c, friendlyFire: true }))}>ВКЛ</button>
                </div>
              </KVRow>
              <KVRow label="Островное присоединение">
                <CycleStepper
                  options={['standard', 'fast', 'lite'] as IslandJoinKey[]}
                  value={displayConfig.islandJoin}
                  display={ISLAND_LABEL[displayConfig.islandJoin]}
                  disabled={guestMode}
                  onChange={(v) => setConfig((c) => ({ ...c, islandJoin: v }))}
                />
              </KVRow>
              <KVRow label="Уровень заражения">
                <Stepper
                  value={displayConfig.infectionPercent}
                  min={0}
                  max={100}
                  step={5}
                  display={`${displayConfig.infectionPercent}%`}
                  disabled={guestMode}
                  onChange={(v) => setConfig((c) => ({ ...c, infectionPercent: v }))}
                />
              </KVRow>
              <KVRow label="Уровень зомби">
                <CycleStepper
                  options={['low', 'mid', 'high'] as LevelKey[]}
                  value={displayConfig.zombieLevel}
                  display={LEVEL_LABEL[displayConfig.zombieLevel]}
                  hot={displayConfig.zombieLevel === 'high'}
                  disabled={guestMode}
                  onChange={(v) => setConfig((c) => ({ ...c, zombieLevel: v }))}
                />
              </KVRow>
              <KVRow label="Уровень лута">
                <CycleStepper
                  options={['low', 'mid', 'high'] as LevelKey[]}
                  value={displayConfig.lootLevel}
                  display={LEVEL_LABEL[displayConfig.lootLevel]}
                  disabled={guestMode}
                  onChange={(v) => setConfig((c) => ({ ...c, lootLevel: v }))}
                />
              </KVRow>
              <KVRow label="Дальность / Туман">
                <CycleStepper
                  options={['low', 'mid', 'high'] as LevelKey[]}
                  value={displayConfig.fogDistance}
                  display={LEVEL_LABEL[displayConfig.fogDistance]}
                  disabled={guestMode}
                  onChange={(v) => setConfig((c) => ({ ...c, fogDistance: v }))}
                />
              </KVRow>
            </div>
          </div>

          <div className="mr-lobby-templates">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#f5ead0', fontWeight: 700 }}>
              ◇ ШАБЛОНЫ ЛОББИ
            </span>
            <span style={{ flex: 1, fontSize: '0.74rem', color: 'rgba(160,172,190,0.7)', textAlign: 'center' }}>
              {guestMode ? 'Настройки задаёт хост лобби' : 'Сохраните настройки как шаблон для быстрого старта'}
            </span>
            <div className="mr-lobby-templates-actions">
              <button type="button" title="Сохранить шаблон" disabled={guestMode} onClick={() => saveTemplate(config)}>↧</button>
            </div>
          </div>
          </div>

          <div
            ref={chatRef}
            className={`mr-lobby-chat-float mr-lobby-chat-float--embedded${chatExpanded ? ' mr-lobby-chat-float--open' : ''}`}
            aria-label="Чат лобби"
          >
            <button
              type="button"
              className="mr-lobby-chat-head"
              onClick={() => setChatExpanded((v) => !v)}
            >
              <span>ЧАТ ЛОББИ</span>
              <span className="mr-lobby-chat-head-pill">
                {visibleChatMessages.filter((m) => !m.system).length} сообщ. {chatExpanded ? '▼' : '▲'}
              </span>
            </button>
            <div className="mr-lobby-chat-msgs" ref={chatScrollRef}>
              {(chatExpanded ? visibleChatMessages : visibleChatMessages.slice(-3)).map((m, i) => (
                <ChatRow key={i} message={m} />
              ))}
            </div>
            <form
              className="mr-lobby-chat-input-row"
              onSubmit={(e) => {
                e.preventDefault();
                void sendChat();
              }}
            >
              <input
                type="text"
                className="mr-lobby-chat-input"
                placeholder="Сообщение в лобби..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onFocus={() => setChatExpanded(true)}
              />
              <button type="submit" className="mr-lobby-chat-send" aria-label="Отправить">➤</button>
            </form>
          </div>
        </section>

        {/* Сторона B */}
        <aside className="mr-lobby-side" aria-label="Сторона В">
          <div className="mr-lobby-side-head mr-lobby-side-head--alt">СТОРОНА В</div>
          <LobbySideBotPanel
            tone="alt"
            disabled={guestMode}
            difficulty={displayConfig.difficulty}
            onDifficulty={(d) => setConfig((c) => ({ ...c, difficulty: d }))}
            onAddBots={(n) => addBotsToSide('B', n)}
            onFillBots={() => fillBots('B')}
          />
          {displaySlotsB.map((slot) => (
            <SlotRow
              key={slot.key}
              kind={slot.kind}
              index={slot.index}
              tone="alt"
              player={slot.player}
              lobbyOwnerUserId={activeServerLobby?.ownerUserId ?? currentUserId}
              viewerUserId={currentUserId}
              prefRole={slotPrefs[slot.index + SLOTS_PER_SIDE]?.role ?? SLOT_ROLE_OPTIONS[0]}
              prefLoadout={slotPrefs[slot.index + SLOTS_PER_SIDE]?.loadout ?? SLOT_LOADOUT_OPTIONS[0]}
              onPrefChange={(patch) =>
                setSlotPrefs((prev) => ({
                  ...prev,
                  [slot.index + SLOTS_PER_SIDE]: {
                    role: SLOT_ROLE_OPTIONS[0],
                    loadout: SLOT_LOADOUT_OPTIONS[0],
                    ...prev[slot.index + SLOTS_PER_SIDE],
                    ...patch,
                  },
                }))
              }
              disabled={guestMode ? Boolean(slot.player || slot.status === 'closed') : Boolean(slot.player && !slot.player.isBot)}
              onCycle={() => {
                const slotIndex = slot.index + SLOTS_PER_SIDE;
                if (guestMode) void takeGuestSlot(slotIndex);
                else cycleSlot('B', slot.index);
              }}
            />
          ))}
        </aside>
      </div>

      {/* Футер: полоска метрик + полноширинная кнопка «Создать лобби» / «Запустить лобби» */}
      <footer className="mr-lobby-footer">
        <div className="mr-lobby-footer-strip">
          <div className="mr-lobby-footer-wait" aria-label="Комната ожидания">
            <span className="mr-lobby-footer-wait-ico" aria-hidden="true">⌛</span>
            <div className="mr-lobby-footer-wait-main">
              <span className="mr-lobby-footer-wait-title">ОЖИДАНИЕ</span>
              {unassignedPlayers.length > 0 ? (
                <div className="mr-lobby-footer-wait-list">
                  {unassignedPlayers.map((player) => (
                    <span key={`${player.userId}-${player.joinedAt}`} className="mr-lobby-footer-wait-chip" title={player.displayName}>
                      {player.displayName.slice(0, 1).toUpperCase()}
                      <span className="mr-lobby-footer-wait-chip-name">{player.displayName}</span>
                    </span>
                  ))}
                </div>
              ) : (
                <span className="mr-lobby-footer-wait-empty">Нет в очереди</span>
              )}
            </div>
          </div>
          <FooterCell label="КОД ЛОББИ">
            <span className="mr-lobby-footer-cell-value">{lobbyCode}</span>
            <button type="button" className="mr-lobby-footer-copy" onClick={copyLobbyCode} title="Скопировать код" disabled={lobbyCode === '—'}>
              ⧉
            </button>
          </FooterCell>
          <div className="mr-lobby-footer-invite-wrap">
            <button type="button" className="mr-lobby-footer-invite" disabled={guestMode} onClick={() => setInvitePopOpen((v) => !v)}>
              👥 ПРИГЛАСИТЬ
            </button>
            {invitePopOpen && !guestMode && (
              <InvitePop
                friends={onlineFriends.length > 0 ? onlineFriends : sortedFriends}
                friendsStatus={props.friendsStatus}
                inviteStatusByFriendId={props.inviteStatusByFriendId}
                onInvite={(f) => props.onInviteFriend(f)}
                onClose={() => setInvitePopOpen(false)}
              />
            )}
          </div>
          <div className="mr-lobby-footer-primary-row">
            <div
              className="mr-lobby-privacy-seg"
              role="group"
              aria-label="Тип лобби при создании"
              data-mode={displayConfig.lobbyType === 'public' ? 'public' : 'private'}
              data-locked={createLobbyBusy || lobbyRegisteredOnServer || guestMode ? 'true' : 'false'}
            >
              <button
                type="button"
                className="mr-lobby-privacy-seg-btn"
                aria-pressed={displayConfig.lobbyType === 'private'}
                disabled={createLobbyBusy || lobbyRegisteredOnServer || guestMode}
                onClick={() => setConfig((c) => ({ ...c, lobbyType: 'private' }))}
              >
                <span aria-hidden>🔒</span>
                ПРИВАТНОЕ
              </button>
              <button
                type="button"
                className="mr-lobby-privacy-seg-btn"
                aria-pressed={displayConfig.lobbyType === 'public'}
                disabled={createLobbyBusy || lobbyRegisteredOnServer || guestMode}
                onClick={() => setConfig((c) => ({ ...c, lobbyType: 'public' }))}
              >
                <span aria-hidden>🌐</span>
                ПУБЛИЧНОЕ
              </button>
            </div>
            <button
              type="button"
              className={`mr-lobby-primary-action ${guestMode || lobbyRegisteredOnServer ? 'mr-lobby-primary-action--launch' : 'mr-lobby-primary-action--create'}`}
              disabled={createLobbyBusy}
              aria-busy={createLobbyBusy}
              onClick={() => void handleFooterPrimary()}
            >
              {createLobbyBusy ? 'СОЗДАНИЕ…' : guestMode ? (!guestHasSlot ? 'ЗАЙМИТЕ СЛОТ' : guestReady ? 'СНЯТЬ ГОТОВНОСТЬ' : 'ГОТОВ') : lobbyRegisteredOnServer ? 'ЗАПУСТИТЬ ЛОББИ' : 'СОЗДАТЬ ЛОББИ'}
            </button>
          </div>
        </div>
        {footerActionError ? (
          <div className="mr-lobby-footer-action-error" role="alert">
            {footerActionError}
          </div>
        ) : null}
      </footer>
        </>
      )}
      </div>

      {/* Скрытое использование MysteryRoomMapCard для совместимости (миниатюра выбранной карты в мобильной выборке)
          оставлено для возможной будущей выборки — сейчас не отрисовывается. */}
      {false && (
        <MysteryRoomMapCard
          title={props.selectedMapTitle}
          mapDefinition={props.selectedMapDefinition}
        />
      )}
    </div>
  );
}

type MapPickerItem = {
  id: string;
  title: string;
  subtitle: string;
  kind: 'builtin' | 'published' | 'my';
  source?: MrMapSummary;
};

function buildMapDescription(selectedId: string, definition: MysteryRoomMapDefinition, source?: MrMapSummary): string {
  const modesNote = formatMysteryRoomPlayableModesShort(source ?? definition);
  let body: string;
  if (selectedId === BUILTIN_MAP_ID) {
    body =
      'Изолированный исследовательский комплекс на астероиде Астерион. Разгерметизация сбоев систем и неизвестная угроза внутри.';
  } else if (selectedId === 'lisad_9_infection_protocol') {
    body = 'Расширенная карантинная карта LISAD-9 почти на всё поле редактора: шлюз, хаб, лаборатория, серверная, медблок, реагенты, охрана, инженерка, архив, тоннели и эвакуация.';
  } else {
    body = source?.description?.trim() || 'Карта сообщества — соберите смену по сети или начните в одиночку.';
  }
  if (modesNote !== 'Все режимы') {
    return `${body} Доступные режимы лобби: ${modesNote}.`;
  }
  return body;
}

function SlotRow({
  kind,
  index,
  tone,
  hostName,
  hostAvatar,
  hostLevel,
  player,
  lobbyOwnerUserId,
  viewerUserId,
  prefRole,
  prefLoadout,
  onPrefChange,
  disabled,
  onCycle,
}: {
  kind: SlotKind;
  index: number;
  tone?: 'alt';
  hostName?: string;
  hostAvatar?: string | null;
  hostLevel?: number;
  player?: ServerLobbySlot['player'] | null;
  lobbyOwnerUserId: number | null;
  viewerUserId: number | null;
  prefRole: string;
  prefLoadout: string;
  onPrefChange: (patch: Partial<{ role: string; loadout: string }>) => void;
  disabled?: boolean;
  onCycle: () => void;
}) {
  const ownerId = lobbyOwnerUserId ?? -1;
  const vid = viewerUserId ?? -1;
  const readyGlow = Boolean(player?.isReady);

  const showExpanded =
    kind !== 'open' &&
    (kind === 'host'
      ? vid === ownerId
      : kind === 'human'
        ? Boolean(player && vid === player.userId)
        : kind === 'bot'
          ? vid === ownerId
          : false);

  const wrapCls = [
    'mr-lobby-slot-expand',
    tone === 'alt' ? 'mr-lobby-slot-expand--alt' : '',
    readyGlow ? 'mr-lobby-slot-expand--ready' : '',
    kind === 'host' ? 'mr-lobby-slot-expand--leader' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const rolePickId = `mr-slot-role-${tone ?? 'a'}-${index}`;
  const loadPickId = `mr-slot-load-${tone ?? 'a'}-${index}`;

  const expandedFields =
    showExpanded ? (
      <div className="mr-lobby-slot-expand-fields" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
        <div className="mr-lobby-slot-expand-field">
          <span className="mr-lobby-slot-expand-field-label">Тип</span>
          <span className="mr-lobby-slot-expand-field-static">
            {kind === 'host' ? 'Лидер' : kind === 'bot' ? 'Бот' : 'Игрок'}
          </span>
        </div>
        <div className="mr-lobby-slot-expand-field">
          <label className="mr-lobby-slot-expand-field-label" htmlFor={rolePickId}>
            Роль
          </label>
          <select
            id={rolePickId}
            className="mr-lobby-slot-expand-select"
            value={prefRole}
            onChange={(e) => onPrefChange({ role: e.target.value })}
          >
            {SLOT_ROLE_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
        <div className="mr-lobby-slot-expand-field">
          <label className="mr-lobby-slot-expand-field-label" htmlFor={loadPickId}>
            Снаряжение
          </label>
          <select
            id={loadPickId}
            className="mr-lobby-slot-expand-select"
            value={prefLoadout}
            onChange={(e) => onPrefChange({ loadout: e.target.value })}
          >
            {SLOT_LOADOUT_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
        <div className="mr-lobby-slot-expand-ready-line">
          <span className="mr-lobby-slot-expand-field-label">Готовность</span>
          <span className={readyGlow ? 'mr-lobby-slot-expand-ready-yes' : 'mr-lobby-slot-expand-ready-no'}>
            {readyGlow ? '✓ Готов' : '○ Не готов'}
          </span>
        </div>
      </div>
    ) : null;

  // Свободное место (слот открыт для игрока)
  if (kind === 'open') {
    return (
      <button type="button" className="mr-lobby-slot mr-lobby-slot--open-free" onClick={onCycle} disabled={disabled}>
        <div className="mr-lobby-slot-avatar" style={{ background: 'transparent', borderStyle: 'dashed' }} aria-hidden="true">
          +
        </div>
        <div style={{ minWidth: 0 }}>
          <div className="mr-lobby-slot-name" style={{ color: 'rgba(180,192,206,0.82)' }}>
            Свободное место
          </div>
          <div className="mr-lobby-slot-status mr-lobby-slot-status--open">Ждёт игрока</div>
        </div>
        <span style={{ width: 22, height: 22, display: 'grid', placeItems: 'center', color: 'rgba(180,192,206,0.55)' }}>👥</span>
      </button>
    );
  }

  if (kind === 'host') {
    const name = player?.displayName ?? hostName;
    return (
      <div className={wrapCls}>
        <button type="button" className="mr-lobby-slot-expand-top mr-lobby-slot-expand-top--leader" onClick={onCycle} disabled>
          <div className="mr-lobby-slot-avatar" aria-hidden="true">
            {hostAvatar && !player ? <img src={hostAvatar} alt="" /> : (name?.slice(0, 1).toUpperCase() || 'H')}
            <span className="mr-lobby-slot-avatar-level">{hostLevel ?? '—'}</span>
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="mr-lobby-slot-name">
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
              <span className="mr-lobby-slot-name-crown" aria-label="Лидер">
                ♛
              </span>
            </div>
            <div className={`mr-lobby-slot-status ${player?.isReady ? 'mr-lobby-slot-status--ready' : 'mr-lobby-slot-status--wait'}`}>
              Лидер{player ? ` · ${player.isReady ? 'Готов' : 'Не готов'}` : ''}
            </div>
          </div>
          <div className="mr-lobby-slot-icons" aria-hidden="true">
            <span className="mr-lobby-slot-icon-online">🎤</span>
            <span className="mr-lobby-slot-icon-online">🖥</span>
          </div>
        </button>
        {expandedFields}
      </div>
    );
  }

  if (kind === 'bot') {
    const names = ['Nighthawk_7', 'Spectre_09', 'IronShade', 'Silent_Reaper', 'RedSector', 'VoidWalker'];
    const name = player?.displayName ?? names[(index + (tone === 'alt' ? 3 : 0)) % names.length];
    return (
      <div className={wrapCls}>
        <button type="button" className="mr-lobby-slot-expand-top" onClick={onCycle} disabled={disabled}>
          <div className="mr-lobby-slot-avatar" aria-hidden="true">
            🤖
            <span className="mr-lobby-slot-avatar-level">{30 + index * 3}</span>
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="mr-lobby-slot-name">
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
            </div>
            <div className="mr-lobby-slot-status mr-lobby-slot-status--bot">
              Бот · {player?.isReady ? 'Готов' : 'Не готов'}
            </div>
          </div>
          <div className="mr-lobby-slot-icons" aria-hidden="true">
            <span className="mr-lobby-slot-icon-mute">🎤</span>
            <span className="mr-lobby-slot-icon-mute">🖥</span>
          </div>
        </button>
        {expandedFields}
      </div>
    );
  }

  // human
  const name = player?.displayName ?? 'Игрок';
  return (
    <div className={wrapCls}>
      <button type="button" className="mr-lobby-slot-expand-top" onClick={onCycle} disabled={disabled}>
        <div className="mr-lobby-slot-avatar" aria-hidden="true">
          {name.slice(0, 1).toUpperCase()}
          <span className="mr-lobby-slot-avatar-level">{hostLevel ?? '—'}</span>
        </div>
        <div style={{ minWidth: 0 }}>
          <div className="mr-lobby-slot-name">
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
          </div>
          <div className={`mr-lobby-slot-status ${player?.isReady ? 'mr-lobby-slot-status--ready' : 'mr-lobby-slot-status--wait'}`}>
            Игрок · {player?.isReady ? 'Готов' : 'Не готов'}
          </div>
        </div>
        <div className="mr-lobby-slot-icons" aria-hidden="true">
          <span className="mr-lobby-slot-icon-online">🎤</span>
          <span className="mr-lobby-slot-icon-online">🖥</span>
        </div>
      </button>
      {expandedFields}
    </div>
  );
}

function ChatRow({ message }: { message: ChatMessage }) {
  if (message.system) {
    return (
      <div className="mr-lobby-chat-row mr-lobby-chat-row--system">
        <div className="mr-lobby-chat-system">
          <span className="mr-lobby-chat-bubble-time">[{formatTs(message.ts)}]</span> {message.text}
        </div>
      </div>
    );
  }
  const initial = message.author.trim().slice(0, 1).toUpperCase() || '?';
  return (
    <div className={`mr-lobby-chat-row${message.mine ? ' mr-lobby-chat-row--mine' : ''}`}>
      <div className="mr-lobby-chat-avatar" aria-hidden="true">
        {message.avatarUrl ? <img src={message.avatarUrl} alt="" /> : initial}
      </div>
      <div className="mr-lobby-chat-bubble">
        <span className="mr-lobby-chat-bubble-author">{message.author}</span>
        {message.text}
        <span className="mr-lobby-chat-bubble-time">[{formatTs(message.ts)}]</span>
      </div>
    </div>
  );
}

function FooterCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mr-lobby-footer-cell">
      <div className="mr-lobby-footer-cell-stack">
        <span className="mr-lobby-footer-cell-label">{label}</span>
        <span className="mr-lobby-footer-cell-stack-row">{children}</span>
      </div>
    </div>
  );
}

function KVRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mr-lobby-kv">
      <span className="mr-lobby-kv-label">{label}</span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{children}</span>
    </div>
  );
}

function Stepper({
  value,
  min,
  max,
  step = 1,
  display,
  disabled,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  step?: number;
  display: string;
  disabled?: boolean;
  onChange: (v: number) => void;
}) {
  return (
    <span className="mr-lobby-kv-stepper">
      <button type="button" disabled={disabled || value <= min} onClick={() => onChange(Math.max(min, value - step))}>◀</button>
      <span className="mr-lobby-kv-value">{display}</span>
      <button type="button" disabled={disabled || value >= max} onClick={() => onChange(Math.min(max, value + step))}>▶</button>
    </span>
  );
}

function CycleStepper<T extends string>({
  options,
  value,
  display,
  hot,
  disabled,
  onChange,
}: {
  options: T[];
  value: T;
  display: string;
  hot?: boolean;
  disabled?: boolean;
  onChange: (v: T) => void;
}) {
  const idx = Math.max(0, options.indexOf(value));
  const change = (delta: number) => {
    const next = options[(idx + delta + options.length) % options.length];
    onChange(next);
  };
  return (
    <span className="mr-lobby-kv-stepper">
      <button type="button" disabled={disabled} onClick={() => change(-1)}>◀</button>
      <span className={`mr-lobby-kv-value ${hot ? 'mr-lobby-kv-value--hot' : ''}`}>{display}</span>
      <button type="button" disabled={disabled} onClick={() => change(1)}>▶</button>
    </span>
  );
}

function InvitePop({
  friends,
  friendsStatus,
  inviteStatusByFriendId,
  onInvite,
  onClose,
}: {
  friends: MrFriend[];
  friendsStatus: string;
  inviteStatusByFriendId: Record<number, string>;
  onInvite: (friend: MrFriend) => void;
  onClose: () => void;
}) {
  const popRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!popRef.current) return;
      if (popRef.current.contains(e.target as Node)) return;
      onClose();
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [onClose]);
  return (
    <div ref={popRef} className="mr-lobby-invite-pop" role="dialog" aria-label="Пригласить друга">
      {friends.length === 0 ? (
        <div style={{ padding: 12, color: 'rgba(180,192,206,0.7)', fontSize: '0.78rem' }}>{friendsStatus}</div>
      ) : (
        friends.slice(0, 12).map((f) => {
          const name = f.display_name || f.username || `user #${f.id}`;
          const status = inviteStatusByFriendId[f.id];
          const sent = status === 'Отправлено';
          const loading = status === 'Отправляю';
          return (
            <div key={f.id} className="mr-lobby-invite-pop-row">
              <div className="mr-lobby-invite-pop-avatar">
                {f.avatar_url ? <img src={f.avatar_url} alt="" /> : name.slice(0, 1).toUpperCase()}
              </div>
              <div className="mr-lobby-invite-pop-name">
                <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                <small>{formatMysteryRoomFriendPresence(f)}</small>
              </div>
              <button
                type="button"
                className="mr-lobby-invite-pop-btn"
                disabled={loading || sent}
                onClick={() => onInvite(f)}
              >
                {sent ? 'OK' : loading ? '...' : 'Позвать'}
              </button>
            </div>
          );
        })
      )}
    </div>
  );
}

function saveTemplate(cfg: LobbyConfig) {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(`${STORAGE_KEY}.template`, JSON.stringify(cfg));
  } catch {
    // шаблоны не критичны
  }
}

function useStoredMenuUser(): StoredMenuUser | null {
  const [user, setUser] = useState<StoredMenuUser | null>(null);
  useEffect(() => {
    const load = () => {
      try {
        const raw = getStoredUserRaw();
        setUser(raw ? (JSON.parse(raw) as StoredMenuUser) : null);
      } catch {
        setUser(null);
      }
    };
    load();
    window.addEventListener('lisad_auth_changed', load);
    return () => window.removeEventListener('lisad_auth_changed', load);
  }, []);
  return user;
}

function resolveMenuUserName(user: StoredMenuUser | null, profile: MrProfileResponse['profile'] | null): string {
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
  if (fullName) return fullName;
  if (user?.display_name) return user.display_name;
  if (user?.username) return user.username;
  if (profile) return `Игрок #${profile.userId}`;
  return 'VoidWalker';
}

function resolveMenuAvatar(user: StoredMenuUser | null): string | null {
  return user?.avatar_display_url || user?.avatar_url || user?.avatar || null;
}

function formatCompact(value: number): string {
  return Math.max(0, Math.round(value)).toLocaleString('ru-RU');
}

function formatTs(ts: number): string {
  const d = new Date(ts);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

// Использование clampWanderBotCount чтобы избежать unused import warning при будущих доработках
void clampWanderBotCount;
